/**
 * Magic Link Authentication Form
 * Handles passwordless authentication flow
 */

import { useState, FormEvent, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  requestMagicLink,
  resetMagicLinkState,
} from "@/store/slices/authSlice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { validateEmail } from "@/utils/validation";
import { Mail, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { detectProduct } from "@/config/product.config";

/**
 * Get the magic link callback URL based on the current product
 */
const getMagicLinkUrl = (): string => {
  const product = detectProduct();

  if (product === "interview") {
    return "https://interview.globalcoders.app";
  }

  // Default to meetings
  return "https://globalcoders.app";
};

export const MagicLinkForm = () => {
  const dispatch = useAppDispatch();
  const { magicLinkStatus, magicLinkError, maskedEmail } = useAppSelector(
    (state) => state.auth
  );

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(0);

  // Countdown timer for rate limiting (60 seconds)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }

    return () => clearTimeout(timer);
  }, [countdown]);

  const validateEmailField = (value: string): boolean => {
    const result = validateEmail(value);

    if (!result.valid) {
      setEmailError(result.message || "Invalid email");
      return false;
    }

    setEmailError(null);
    return true;
  };

  const handleEmailBlur = () => {
    if (email) {
      validateEmailField(email);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateEmailField(email)) {
      return;
    }

    const url = getMagicLinkUrl();
    const result = await dispatch(requestMagicLink({ email, url }));

    // If successful, start 60s countdown
    if (requestMagicLink.fulfilled.match(result)) {
      setCountdown(60);
    }
  };

  const handleResend = async () => {
    const url = getMagicLinkUrl();
    const result = await dispatch(requestMagicLink({ email, url }));

    if (requestMagicLink.fulfilled.match(result)) {
      setCountdown(60);
    }
  };

  const handleReset = () => {
    setEmail("");
    setEmailError(null);
    setCountdown(0);
    dispatch(resetMagicLinkState());
  };

  // SUCCESS STATE - Email sent
  if (magicLinkStatus === "request-success") {
    return (
      <div className="w-full max-w-md space-y-6 animate-fade-in">
        <Alert className="border-green-200 bg-green-50 dark:bg-green-950/30">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
          <AlertDescription className="ml-2">
            <div className="space-y-2">
              <p className="font-semibold text-green-900 dark:text-green-100">
                Check your email!
              </p>
              <p className="text-sm text-green-800 dark:text-green-200">
                We've sent a magic link to:
              </p>
              <p className="text-sm font-mono font-semibold text-green-900 dark:text-green-100">
                {maskedEmail}
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                Click the link in the email to sign in instantly. The link is
                valid for 10 minutes.
              </p>
            </div>
          </AlertDescription>
        </Alert>

        <div className="space-y-3">
          <Button
            onClick={handleResend}
            disabled={countdown > 0}
            variant="outline"
            className="w-full"
          >
            {countdown > 0
              ? `You can resend in ${countdown} seconds`
              : "Resend Magic Link"}
          </Button>

          <Button onClick={handleReset} variant="ghost" className="w-full">
            Use different email
          </Button>
        </div>
      </div>
    );
  }

  // ERROR STATE
  if (magicLinkStatus === "request-error" && magicLinkError) {
    return (
      <div className="w-full max-w-md space-y-6 animate-fade-in">
        <Alert variant="destructive">
          <AlertCircle className="h-5 w-5" />
          <AlertDescription className="ml-2">
            <div className="space-y-2">
              <p className="font-semibold">Something went wrong</p>
              <p className="text-sm">{magicLinkError}</p>
            </div>
          </AlertDescription>
        </Alert>

        <Button onClick={handleReset} variant="outline" className="w-full">
          Try Again
        </Button>
      </div>
    );
  }

  // FORM STATE (idle or requesting)
  return (
    <form onSubmit={handleSubmit} className="w-full max-w-md space-y-4">
      <div className="space-y-2">
        <Label htmlFor="magic-link-email" className="text-sm font-medium">
          Work Email
        </Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="magic-link-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={handleEmailBlur}
            placeholder="you@example.com"
            disabled={magicLinkStatus === "requesting"}
            className={`pl-10 ${emailError ? "border-destructive" : ""}`}
            required
            aria-invalid={!!emailError}
            aria-describedby={emailError ? "email-error" : undefined}
          />
        </div>
        {emailError && (
          <p id="email-error" className="text-sm text-destructive" role="alert">
            {emailError}
          </p>
        )}
      </div>

      <Button
        type="submit"
        disabled={magicLinkStatus === "requesting" || !!emailError || !email}
        className="w-full"
      >
        {magicLinkStatus === "requesting" ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            Send Magic Link
            <span className="ml-2">→</span>
          </>
        )}
      </Button>
    </form>
  );
};
